import { useStore } from '@nanostores/react';
import { Root, Item } from '@radix-ui/react-radio-group';
import { memo, useMemo } from 'react';

import { SectionRow } from '~/components/dialogs/settings/SectionContainer';
import { Link } from '~/components/ui/Link';
import { useGithubStore } from '~/lib/context/github';
import { useProjectVisibility } from '~/lib/hooks/useProjectVisibility';
import { projectStore } from '~/lib/stores/project';
import { userStore } from '~/lib/stores/user';
import { classNames } from '~/utils/classNames';
import { visibilityOptions, type VisibilityOption } from '~/utils/projectVisibility';

export const ProjectVisibility = memo(({ canChangeVisibility }: { canChangeVisibility: boolean }) => {
  const activeOrganization = useStore(userStore.getActiveOrganization());
  const visibility = useStore(projectStore.visibility);
  const maxVisibility = activeOrganization?.organizationSettings?.maxVisibility ?? 'public';
  const github = useGithubStore();
  const githubConnectedTo = useStore(github.connection);
  const githubState = useStore(github.state);

  const options = useMemo(() => {
    let changedOptions = [...visibilityOptions];

    if (maxVisibility === 'private') {
      changedOptions = changedOptions.filter((option) => option.value === 'private');
    } else if (maxVisibility === 'secret') {
      changedOptions = changedOptions.filter((option) => option.value !== 'public');
    }

    return changedOptions.map((item, index) => <VisibilityItem key={`${item.value}-${index}`} item={item} />).reverse();
  }, [maxVisibility]);

  const { onProjectVisibilityChange } = useProjectVisibility();

  let content = (
    <Root
      name="project-visibility"
      defaultValue={visibility}
      className="flex w-full flex-col md:flex-row md:justify-between gap-4 [&:has(>:only-child)>*]:w-[200px] [&:not(:has(>:only-child))>*]:flex-1"
      onValueChange={onProjectVisibilityChange}
    >
      {options}
    </Root>
  );

  if (githubConnectedTo.repo) {
    const repository = githubState.integration!.repository;
    content = (
      <p className="text-bolt-elements-text-secondary w-2/3">
        The GitHub integration is active on this project. Its visibility is set on your{' '}
        <Link
          className="decoration-none"
          href={`https://github.com/${repository.owner}/${repository.name}/settings`}
          target="_blank"
          rel="noopener noreferrer"
        >
          GitHub repository setting
        </Link>
        .
      </p>
    );
  } else if (!canChangeVisibility) {
    content = (
      <p className="text-bolt-elements-text-secondary w-2/3">
        You do not have permission to change the visibility of this project.
      </p>
    );
  }

  return (
    <SectionRow
      title={githubConnectedTo.repo ? 'Project visibility (Manage on Github)' : 'Project Visibility'}
      layout="vertical"
      innerClassName={classNames('flex flex-col', githubConnectedTo.repo ? 'gap-2.5' : 'gap-4')}
      titleClassName="font-semibold text-sm mb-0"
      action={content}
    />
  );
});

ProjectVisibility.displayName = 'ProjectVisibility';

const VisibilityItem = memo(({ item }: { item: VisibilityOption }) => {
  return (
    <Item
      className={classNames(
        'group flex flex-col gap-1 p-4 border border-bolt-elements-borderColor min-w-[160px] bg-bolt-ds-surfaceOne rounded-[8px] outline-none',
        'hover:bg-bolt-elements-item-backgroundActive focus-visible:ring-2 focus-visible:ring-bolt-elements-item-backgroundActive',
        'data-[state=checked]:bg-bolt-ds-brand/10 data-[state=checked]:border-bolt-ds-brand/10 hover:data-[state=checked]:bg-bolt-ds-brand/10',
      )}
      value={item.value}
    >
      <div className="flex gap-1 items-center font-medium group-data-[state=checked]:text-bolt-ds-brand">
        <span className={`${item.iconClassName} block size-4`} />
        <p>{item.label}</p>
      </div>
      <p className="text-bolt-elements-text-secondary lg:text-sm text-xs text-start">
        {item.projectSettingsDescription}
      </p>
    </Item>
  );
});

VisibilityItem.displayName = 'VisibilityItem';
